import type { CSSProperties } from 'react';

import { isWrappableTextColumn } from '../../../stores/utils/column-edit-kind';

// PSTUDIO-4026: helper that keeps entering edit on a wrapped cell from
// growing the row (and thereby jumping the virtualized workflows table).
//
// The auto-grown edit <textarea> used to expand the row from the wrap clamp to the
// full text height in one step, so the virtualizer re-measured and shifted every
// following row. Clamping the textarea to the SAME height the read view already
// uses removes that. (The other half of the fix — focus({ preventScroll: true })
// plus an explicit, header-aware re-scroll via computeRevealScrollDelta — lives at
// the cell.tsx call sites.)
//
// Pure so it can be unit-tested without a DOM (pro-studio has no RTL — React logic
// is exercised through pure helpers).

// The read-view clamp for any wrapped text cell (mirrors `cellViewWrapStyle` in
// cell.tsx). Applying the identical value to the edit <textarea> gives full
// view↔edit height parity, so edit entry never changes the row height.
export const TEXT_WRAP_MAX_HEIGHT =
  'max(80px, var(--c-editor-table-tr-height, 80px))';

// PSTUDIO-2870, widened by PSTUDIO-4087 — wrapped text-cell view style. When the column's persistent
// wrap toggle is on, the view <span> wraps onto multiple lines instead of
// clamping to one line with an ellipsis. Kept as a module-level constant (never
// rebuilt per render) so `cell.tsx` hands the same object to every wrapped view.
//   - `whiteSpace: 'normal'` — wrap instead of `nowrap`.
//   - NO `textOverflow: 'ellipsis'` — meaningless once wrapping.
//   - `overflow: 'hidden'` + `maxHeight` — the row grows to fit up to the clamp,
//     then clips (full text stays available via the `title` tooltip).
//   - `maxHeight: max(80px, var(--c-editor-table-tr-height, 80px))` — the DEFAULT
//     clamp is 80px = 4 lines × 20px Figma line-height (`Body/Body 3`, node
//     4474-58811, get_variable_defs; the clamp itself isn't drawn in that frame —
//     every cell there is ≤40px — so per Mostafa's ticket guidance it is 4 lines).
//     BUT a MANUALLY resized row sets `--c-editor-table-tr-height` on the <tr>
//     (draggable-row.tsx — only present when node.settings.rowHeight is set), and
//     the ticket + Leonardo's note require "resize the cell to see more" to keep
//     working WITH wrap: so the clamp is `max(80px, that row height)`. A row
//     stretched to e.g. 200px lets the wrapped text fill 200px (no premature clip
//     + empty gap); an unresized row (var absent → fallback 80px) clamps at 80;
//     shrinking below 80 is ignored (wrap still shows its 4 lines). This is the
//     PSTUDIO-2870 "resized rows × wrap" interaction the cross-boundary review flagged.
//     no token: 4-line clamp derived from Figma line-height 20px (4474-58811),
//     the line COUNT has no DS token.
export const TEXT_WRAP_VIEW_STYLE: CSSProperties = {
  paddingInline: '8px',
  font: 'inherit',
  color: 'inherit',
  overflow: 'hidden',
  whiteSpace: 'normal',
  // `white-space: normal` breaks only where the text offers a break point, and a
  // value with none (a `|`-separated Variables list, a URL, an id) is one word:
  // it stayed on a single clipped line while the edit <textarea>, whose computed
  // `overflow-wrap` is `break-word`, showed it over several. Give the view the
  // same value so a wrapped cell reads the same before and after a double-click.
  // `break-word` rather than `anywhere`: it is the value the textarea computes,
  // and it breaks a word only when the word cannot fit on a line of its own.
  overflowWrap: 'break-word',
  // PSTUDIO-4026: single source for the wrap clamp, shared with the edit
  // <textarea> (resolveEditorCellMaxHeight) so view and edit heights match.
  maxHeight: TEXT_WRAP_MAX_HEIGHT,
};

/**
 * The max-height to apply to an edit-mode textarea, or `undefined` for no clamp.
 * Any column whose persistent wrap is ON is clamped to the same height as its
 * read view, so entering edit does not change the row height. Wrap OFF keeps
 * the unbounded auto-grow used while a double-click edit is open.
 */
export function resolveEditorCellMaxHeight(opts: {
  wrapEnabled: boolean;
}): string | undefined {
  return opts.wrapEnabled ? TEXT_WRAP_MAX_HEIGHT : undefined;
}

// Message and utterances already stored newlines when they were textareas.
// Every other free-text column was a single-line input. Cmd/Ctrl+Enter and a
// multiline paste into the shared growing editor must not start persisting
// newlines there. Plain Enter is committed by the capture handler in table.tsx
// and never reaches this helper.
const NEWLINE_COLUMNS = new Set(['message', 'utterances']);

export function columnStoresNewlines(columnId: string): boolean {
  return NEWLINE_COLUMNS.has(columnId);
}

export function draftWithoutStoredNewlines(value: string): string {
  return value.replace(/\r?\n/g, '');
}

export type EditorWrapClose = 'commit' | 'cancel' | 'incidental';

/**
 * The grown editor collapses when the edit session ends (submit or cancel).
 * An incidental blur leaves the session open, so the cell stays wrapped.
 */
export function shouldCollapseEditorWrap(close: EditorWrapClose): boolean {
  return close !== 'incidental';
}

export function classifyEditorWrapClose(opts: {
  stillEditingThisCell: boolean;
  intent: 'commit' | 'cancel' | null;
}): EditorWrapClose {
  if (opts.stillEditingThisCell) return 'incidental';
  if (opts.intent === 'cancel') return 'cancel';
  return 'commit';
}

export type TextCellPresentation =
  | { control: 'json-view'; wrap: boolean }
  | { control: 'wrapped-view' }
  | { control: 'growing-editor'; clamp: boolean }
  | { control: 'plain' };

/**
 * How a text cell paints. JSON columns stay a view (the panel edits them).
 * An active edit of any other wrappable column grows a wrapping editor.
 * Persistent wrap paints the view wrapped even when the cell is not editing.
 */
export function resolveTextCellPresentation(opts: {
  columnId: string;
  activelyEditing: boolean;
  wrapEnabled: boolean;
  jsonColumn: boolean;
}): TextCellPresentation {
  if (!isWrappableTextColumn(opts.columnId)) return { control: 'plain' };
  if (opts.jsonColumn) return { control: 'json-view', wrap: opts.wrapEnabled };
  if (opts.activelyEditing) {
    return { control: 'growing-editor', clamp: opts.wrapEnabled };
  }
  if (opts.wrapEnabled) return { control: 'wrapped-view' };
  return { control: 'plain' };
}
